import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { generateGUID } from "../../utils/guid";
import { useUser } from "../../context/UserContext";
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Button from '../UI/Button';

const NfcTagWriter = () => {
    const { hasPermission, logout } = useUser();
	const canWrite = hasPermission("nfc", "write");

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm();
    const [message, setMessage] = useState("");
    const [writing, setWriting] = useState(false);
	const [error, setError] = useState("");
    const handleRealWrite = handleSubmit(async (data) => {
		if (!canWrite) {
			setError("У вас нет прав на запись");
			return;
		}
		
        if (!("NDEFReader" in window)) {
            alert("❌ Web NFC не поддерживается в этом браузере.");
            return;
        }

        try {
            setWriting(true);
            const ndef = new window.NDEFReader();
            await ndef.scan();

            const tagId = generateGUID();

            await ndef.write(tagId); // Physical entry to a label

            const payload = { tagId, ...data };


            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/nfc-checkpoints`,
                {
                    method: "POST",
                    body: JSON.stringify(payload),
                },
                logout
            );

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(errText || "Ошибка на сервере");
            }

            setMessage(`✅ Метка записана и сохранена! ID: ${tagId}`);
            reset();
        } catch (err) {
            console.error("Ошибка при записи:", err);
            setMessage(`❌ Ошибка: ${err.message}`);
        } finally {
            setWriting(false);
        }
    });

    const handleEmulateWrite = handleSubmit(async (data) => {
		if (!canWrite) {
			setError("У вас нет прав на запись");
			return;
		}

        try {
            setWriting(true);
            const tagId = generateGUID();
            const payload = { tagId, ...data };

            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/nfc-checkpoints`,
                {
                    method: "POST",
                    body: JSON.stringify(payload),
                },
                logout
            );

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(errText || "Ошибка на сервере");
            }

            setMessage(`🧪 Метка эмулирована и сохранена! ID: ${tagId}`);
            reset();
        } catch (err) {
            console.error("Ошибка эмуляции:", err);
            setMessage(`❌ Ошибка: ${err.message}`);
        } finally {
            setWriting(false);
        }
    });

    return (
        <form
            className="max-w-2xl mx-auto mt-8 bg-[#252526] border border-[#3E3E42] rounded-lg p-6 shadow text-white"
            onSubmit={(e) => e.preventDefault()}
        >
            <h2 className="text-2xl font-semibold mb-6 text-[#CCCCCC]">Создание NFC метки</h2>

			{error && (
				<div className="mt-4 p-3 bg-[#1E1E1E] border border-[#3E3E42] rounded">
					<p className="text-red-400 text-sm">❌ {error}</p>
				</div>
			)}

            <div className="space-y-4">
                <div>
                    <input
                        {...register("name", { required: true })}
                        placeholder="Название метки"
                        className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                    />
                    {errors.name && (
                        <p className="text-red-400 text-sm mt-1">Укажите название</p>
                    )}
                </div>

                <input
                    {...register("description")}
                    placeholder="Описание"
                    className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                />

                <div>
                    <select
                        {...register("type", { required: true })}
                        className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                    >
                        <option value="">-- Тип --</option>
                        <option value="entry">Вход</option>
                        <option value="exit">Выход</option>
                    </select>
                    {errors.type && (
                        <p className="text-red-400 text-sm mt-1">Выберите тип</p>
                    )}
                </div>

                <input
                    {...register("location")}
                    placeholder="Локация"
                    className="w-full bg-[#1E1E1E] text-white p-2 rounded border border-[#555]"
                />
            </div>

            <div className="flex gap-4 mt-6">
                <Button
                    onClick={handleRealWrite}
                    type="button"
                    disabled={writing}
                    variant="primary"
                    className="w-1/2"
                >
                    {writing ? "Ожидание метки..." : "📡 Записать метку"}
                </Button>

                <Button
                    onClick={handleEmulateWrite}
                    type="button"
                    disabled={writing}
                    variant="secondary"
                    className="w-1/2"
                >
                    {writing ? "Сохранение..." : "🧪 Эмулировать"}
                </Button>
            </div>

            {message && (
                <div className="mt-6 p-4 border border-[#3E3E42] rounded bg-[#1E1E1E] text-[#CCCCCC] text-sm">
                    {message}
                </div>
            )}
        </form>
    );
};

export default NfcTagWriter;
